/**
 * QA API istemcisi — fetch tabanlı, JWT'yi localStorage'dan okur.
 * Vite dev sunucusu /v1 isteklerini Fastify'a proxy'ler (vite.config.ts).
 */

const TOKEN_KEY = 'se_qa_token';
const USER_KEY = 'se_qa_user';

export interface CurrentUser {
  sub: string;
  name: string;
  email: string;
  role: 'ADMIN' | 'QA_MANAGER' | 'AUDITOR' | 'READ_ONLY';
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearAuth(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

export function getStoredUser(): CurrentUser | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as CurrentUser;
  } catch {
    return null;
  }
}

export function setStoredUser(user: CurrentUser): void {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Hata gövdesinden kullanıcıya gösterilecek Türkçe mesajı çıkarır.
 * API sözleşmesi: `{title, status}` çekirdeği + varsa `detail` (ör. 400
 * doğrulama, MES 502/404; `plugins/error-handler.ts`). `message` yalnızca
 * geriye dönük destek. Metin gövde `<` ile başlıyorsa (ters vekilin HTML
 * sayfası) ham basılmaz, `HTTP <kod>` döner; diğer metinler olduğu gibi gösterilir.
 */
export function messageFromBody(body: unknown, status: number): string {
  if (typeof body === 'string') {
    const text = body.trim();
    if (text.startsWith('<')) return `HTTP ${status}`;
    if (text.length > 0) return body;
  }
  if (body !== null && typeof body === 'object') {
    const b = body as Record<string, unknown>;
    const str = (v: unknown): string | null =>
      typeof v === 'string' && v.trim().length > 0 ? v : null;
    const parts = [str(b.title), str(b.detail)].filter((v): v is string => v !== null);
    if (parts.length > 0) return parts.join(' — ');
    const legacy = str(b.message);
    if (legacy) return legacy;
  }
  return `HTTP ${status}`;
}

export interface ApiRequestInit extends Omit<RequestInit, 'body' | 'headers'> {
  body?: unknown;
  headers?: Record<string, string>;
  /** Yanıtı blob olarak al (PDF indirme). */
  raw?: boolean;
}

async function request<T>(path: string, init: ApiRequestInit = {}): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    Accept: init.raw ? 'application/octet-stream' : 'application/json',
    ...(init.headers ?? {}),
  };
  if (init.body !== undefined && !(init.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const fetchInit: RequestInit = { headers };
  if (init.method !== undefined) fetchInit.method = init.method;
  if (init.signal !== undefined) fetchInit.signal = init.signal;
  if (init.credentials !== undefined) fetchInit.credentials = init.credentials;
  if (init.body !== undefined) {
    fetchInit.body =
      init.body instanceof FormData ? init.body : JSON.stringify(init.body);
  }
  const res = await fetch(path, fetchInit);

  if (!res.ok) {
    // Gövde okunamasa (bağlantı koptu, iptal) bile oturum düşsün diye gövdeden önce.
    if (res.status === 401) {
      clearAuth();
    }
    // Gövde akışı bir kez okunabilir: json()'dan sonra text() TypeError verir.
    const text = await res.text();
    let detail: unknown = null;
    if (text.trim().length > 0) {
      try {
        detail = JSON.parse(text);
      } catch {
        detail = text;
      }
    }
    throw new ApiError(messageFromBody(detail, res.status), res.status, detail);
  }

  if (init.raw) return (await res.blob()) as unknown as T;
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get('content-type') ?? '';
  if (!ct.includes('application/json')) return (await res.text()) as unknown as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string, init?: ApiRequestInit) => request<T>(path, { ...init, method: 'GET' }),
  post: <T>(path: string, body?: unknown, init?: ApiRequestInit) =>
    request<T>(path, { ...init, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, init?: ApiRequestInit) =>
    request<T>(path, { ...init, method: 'PATCH', body }),
  delete: <T>(path: string, init?: ApiRequestInit) =>
    request<T>(path, { ...init, method: 'DELETE' }),
};

/** Blob'u tarayıcıda indir. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
